import { FC } from 'react'
import { Reveal, ThemeObject, themes } from '@evervault/react'
import { useNavigate } from 'react-router-dom'
import './card.css'

// COMPONENTS
import { Column } from '@components/templates/column'
import Button from '@components/atoms/button'

// TYPES
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { EvervaultAPI } from '@apis'
import { createAuthenticatedLoader, useLoader } from '@utils/loaders'
import { useGroupLoaderData } from '..'


const theme = themes.clean({
    styles: {
        '#root > div': {
            'font-size': '1.25rem',
            'font-family': 'inherit',
            'font-weight': 'bold',
            'color': 'white',
        },
        'button': {
            'transition': 'all 0.3s ease',
            'border-radius': '5px',
            'border': '1px solid transparent',
            'font-family': 'inherit',
            'font-size': 'inherit',
            'font-weight': 'bold',
            'cursor': 'pointer',
            'border-style': 'solid',
            'position': 'relative',
            'text-shadow': 'none',
            'outline': 'none',
            'border-width': '1px',
            '-webkit-appearance': 'button',
            'appearance': 'button',
            'padding': '0.8em 1em',
            'border-color': 'rgb(0, 0, 0)',
            'background': 'rgb(255, 255, 255)',
            'color': 'rgb(0, 0, 0)',
            'box-sizing': 'border-box',

            'height': '100vh',
            'width': '100vw',
        }
    }
})

interface Props { }

const GroupCardScreen: FC<Props> = () => {
    const group = useGroupLoaderData()
    const { collectedAmount } = group
    const request = useLoader<typeof loader>()
    const navigate = useNavigate()

    const { organizer } = group
    const organizerName = `${organizer?.givenName || ''} ${organizer?.familyName || ''}`.trim()

    function handleBack() {
        navigate(-1)
    }

    return (
        <Column.Content className="group-card-screen">
            <Reveal request={request}>
                <Column.Header.Start>
                    <a onClick={handleBack}>
                        <img src="/icons/arrow-left.svg" alt="back" style={{ width: '1.4rem', height: '1.4rem' }} draggable="false" />
                    </a>
                </Column.Header.Start>
                <h2>Your Group Card is Ready</h2>
                <p className="text-body-2">
                    Below is your one time use group card details. Everyones payment methods have been pre-authorized
                    for their maximum payment amounts. After this card is used, everyone's charge will settle.
                </p>
                <div className="group-card-container select-none" style={{ gap: '0.75rem' }}>
                    <img className="payment-brand-img" src="/payment-brands/mastercard-icon.svg" draggable="false" />
                    <div className="flex flex-col gap-2">
                        <h2>{organizerName}</h2>
                        <ProxyText placeholder="XXXX XXXX XXXX XXXX " path="$.card.cardNumber" format={{ regex: /(.{4})/g, replace: '$1 ' }} />
                    </div>
                    <div className="flex flex-row flex-wrap items-center" style={{ gap: 'inherit' }}>
                        <div>
                            <p className="text-body-2">Expiration</p>
                            <div className="flex flex-row items-center justify-start">
                                <ProxyText placeholder="MM" path="$.card.expirationMonth" />
                                <h2>/</h2>
                                <ProxyText placeholder="YYYY" path="$.card.expirationYear" />
                            </div>
                        </div>
                        <div>
                            <p className="text-body-2">CVV</p>
                            <ProxyText placeholder="----" path="$.card.cardSecurityCode" />
                        </div>
                    </div>
                    <img className="pinned" src="/splitcard/symbol/white.svg" draggable="false" />
                </div>
                <h2 className="flex flex-row items-center justify-between">
                    <span>Remaining Funds</span>
                    <ProxyText placeholder={`$${collectedAmount}.00` || ''} path="$.card.formattedAmounts.currentAmount" color="black" />
                </h2>
                <ProxyButton path="$.card.cardNumber">Copy Card Number</ProxyButton>
                <Column.Footer>
                    <Button type="button" onClick={handleBack}>View Dashboard</Button>
                </Column.Footer>
            </Reveal>
        </Column.Content >
    )
}

const ProxyText: FC<{ placeholder?: string, path: string, format?: { regex: RegExp, replace: string, }, color?: string }> = (props) => {
    const { placeholder = '', path, format, color = 'white' } = props

    return (
        <div style={{ fontSize: '1.375rem', width: `${placeholder.length || 16}ch`, maxWidth: '100%', height: '24px' }}>
            <div style={{ display: 'inline-block' }}>
                <Reveal.Text theme={{
                    ...theme, styles: {
                        '#root > div': {
                            'font-size': '1.25rem',
                            'font-family': 'inherit',
                            'font-weight': 'bold',
                            'color': color,
                            'text-align': color === 'black' ? 'right' : 'left'
                        },
                        ...(theme as ThemeObject).styles,
                    }
                }} path={path} format={format} />
            </div>
        </div>
    )
}
const ProxyButton: FC<{ path: string, children: string }> = (props) => {
    const { path } = props
    const buttonText: any = { text: 'Copy Card Number' }

    return (
        <div className="proxy-button" style={{ height: '100%', maxHeight: '52px' }}>
            <Reveal.CopyButton theme={theme} path={path} {...buttonText} />
        </div>
    )
}

const loader = createAuthenticatedLoader(({ params }) => EvervaultAPI.GetCardRequest.init(params.groupId || '', {
    headers: {
        "Access-Control-Allow-Methods": "POST, GET",
        "Access-Control-Allow-Origin": "https://ui-components.evervault.com",
    }
}))

const CardRoute: RouteObject = {
    path: "card",
    element: <GroupCardScreen />,
    loader,
}

export default CardRoute